import React, { useState } from "react";
import { Button, Empty, Select, Spin } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { map } from "lodash";
import { EComModal } from "components/EComModal";
import { AiccSandboxRender } from "types/model/aiccPromptSandbox";
import PromptEditor from "../PromptEditor";
import { renderedSegments } from "./renderedSegments";
import {
  NOT_SIMULATED_VARIABLES,
  RENDERED_EMPTY,
  RENDERED_PROMPTS_TITLE,
  RENDERED_PROMPT_OPTION,
  RENDERED_PROMPT_UNSEEDED,
  VIEW_RENDERED_PROMPTS,
} from "./configs";
import styles from "./Sandbox.module.scss";

const RENDERED_BODY_STYLE = { height: "70vh", overflowY: "auto" as const };

interface Props {
  render?: AiccSandboxRender;
  isLoading: boolean;
  onOpen: () => void;
}

function RenderedPrompts({ render, isLoading, onOpen }: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();

  const prompts = render?.prompts ?? [];
  const selected = prompts.find(({ templateId }) => templateId === selectedId) ?? prompts[0];

  const open = () => {
    onOpen();
    setIsVisible(true);
  };

  const content = () => {
    if (isLoading && !render) {
      return <Spin size="small" />;
    }

    if (!selected) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={RENDERED_EMPTY} />;
    }

    if (!selected.rendered) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={RENDERED_PROMPT_UNSEEDED} />;
    }

    return (
      <div className={styles.renderedPrompt}>
        {renderedSegments(selected.rendered).map(({ kind, text }, index) =>
          kind === "code" ? (
            <pre key={`${selected.templateId}:${index}`} className={styles.renderedCode}>
              {text}
            </pre>
          ) : (
            <PromptEditor key={`${selected.templateId}:${index}`} value={text} rules={{}} readOnly autoHeight />
          )
        )}
      </div>
    );
  };

  return (
    <>
      <Button type="link" className={styles.factsButton} icon={<FileTextOutlined />} onClick={open}>
        {VIEW_RENDERED_PROMPTS}
      </Button>

      <EComModal
        visible={isVisible}
        title={
          <div className={styles.renderedTitle}>
            {RENDERED_PROMPTS_TITLE}
            {!!prompts.length && (
              <Select
                className={styles.renderedSelect}
                aria-label={RENDERED_PROMPTS_TITLE}
                value={selected?.templateId}
                loading={isLoading}
                options={map(prompts, ({ templateId, displayName, source, flowVersion }) => ({
                  label: RENDERED_PROMPT_OPTION(displayName, source, flowVersion),
                  value: templateId,
                }))}
                onChange={setSelectedId}
              />
            )}
          </div>
        }
        width={800}
        bodyStyle={RENDERED_BODY_STYLE}
        footer={null}
        onCancel={() => setIsVisible(false)}
      >
        {content()}

        {!!render?.notSimulatedVariables.length && (
          <div className={styles.renderedNote}>{NOT_SIMULATED_VARIABLES(render.notSimulatedVariables)}</div>
        )}
      </EComModal>
    </>
  );
}

export default RenderedPrompts;
